import { supabase } from '../lib/supabase';
import { AreaClienteStats, SolicitacaoFretePayload, ClientProfile } from '../types/areaCliente';
import { OrdemDeServico } from '../types/ordemServico';
import { Medicoes } from '../types/medicao';
import { Conta } from '../types/financeiro';
import { propostaMetaService } from './propostaMetaService';
import { sanitizeDeep } from '../utils/textSanitizer';

export const areaClienteService = {
  /**
   * Retorna lista de clientes cadastrados no sistema
   */
  async getClientes(): Promise<ClientProfile[]> {
    try {
      // Tenta buscar colunas padrão da tabela pessoa
      const { data, error } = await supabase
        .from('pessoa')
        .select('id, nome, documento, email, fone')
        .order('nome', { ascending: true });

      if (error) {
        console.error('Erro ao buscar lista de clientes:', error);
        return [];
      }

      const list = (data || []).map(p => ({
        id: p.id,
        nome: (p as any).nome_fantasia ? `${p.nome} (${(p as any).nome_fantasia})` : p.nome,
        documento: p.documento,
        email: p.email,
        fone: p.fone,
        nomeFantasia: (p as any).nome_fantasia
      }));

      return sanitizeDeep(list);
    } catch (err) {
      console.error('Erro inesperado em getClientes:', err);
      return [];
    }
  },

  /**
   * Retorna os dados do cliente selecionado
   */
  async getClienteById(pessoaId: number): Promise<ClientProfile | null> {
    try {
      const { data, error } = await supabase
        .from('pessoa')
        .select('id, nome, documento, email, fone')
        .eq('id', pessoaId)
        .maybeSingle();

      if (error || !data) return null;

      return sanitizeDeep({
        id: data.id,
        nome: data.nome,
        documento: data.documento,
        email: data.email,
        fone: data.fone,
        nomeFantasia: (data as any).nome_fantasia
      });
    } catch (err) {
      console.error('Erro ao buscar cliente por ID:', err);
      return null;
    }
  },

  /**
   * Busca as propostas / contratos vinculados ao cliente
   */
  async getPropostasCliente(pessoaId: number): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('pedido')
        .select(`
          *,
          pessoa(id, nome, documento, email),
          detalhes_do_pedido(id, servicos, valor_total, quantidade, valor_unitario, tipo_servico_id)
        `)
        .eq('pessoa_id', pessoaId)
        .order('id', { ascending: false });

      if (error) {
        console.error('Erro ao buscar propostas do cliente:', error);
        return [];
      }
      return sanitizeDeep((data || []).map(p => propostaMetaService.enrichProposta(p)));
    } catch (err) {
      console.error('Erro inesperado em getPropostasCliente:', err);
      return [];
    }
  },

  /**
   * Busca todas as Ordens de Serviço vinculadas aos pedidos do cliente
   */
  async getOrdensServicoCliente(pessoaId: number): Promise<OrdemDeServico[]> {
    try {
      // 1. Obter IDs dos pedidos do cliente
      const { data: pedidos, error: pedErr } = await supabase
        .from('pedido')
        .select('id')
        .eq('pessoa_id', pessoaId);

      if (pedErr) {
        console.error('Erro ao obter pedidos do cliente:', pedErr);
      }

      const pedidoIds = (pedidos || []).map(p => p.id);

      if (pedidoIds.length === 0) {
        return [];
      }

      // 2. Obter ordens de serviço desses pedidos
      const { data: osData, error: osErr } = await supabase
        .from('ordem_de_servico')
        .select('*')
        .in('pedido_id', pedidoIds)
        .order('id', { ascending: false });

      if (osErr) {
        console.error('Erro ao buscar OS do cliente:', osErr);
        return [];
      }

      return sanitizeDeep((osData || []) as OrdemDeServico[]);
    } catch (err) {
      console.error('Erro inesperado em getOrdensServicoCliente:', err);
      return [];
    }
  },

  /**
   * Registra a assinatura digital do cliente numa OS que está "Aguardando
   * Assinatura" (status_id = 6), feita remotamente pelo Painel do Cliente —
   * normalmente a partir do link recebido por notificação de WhatsApp.
   * Transiciona a OS para "Finalizado" (status_id = 4), igual ao fluxo já
   * usado quando o motorista captura a assinatura presencialmente.
   */
  async assinarOrdemServico(osId: number, dados: { assinatura: string; nomeSignatario: string; documentoSignatario?: string }): Promise<boolean> {
    try {
      let ipAssinatura: string | null = null;
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        if (res.ok) {
          const json = await res.json();
          ipAssinatura = json?.ip || null;
        }
      } catch {
        // Falha ao obter IP não deve impedir a assinatura de ser registrada.
      }

      // rg_assinatura é uma coluna numérica no banco (double precision) —
      // mantém apenas os dígitos do documento informado para evitar erro de tipo.
      const documentoNumerico = (dados.documentoSignatario || '').replace(/\D/g, '');

      const { error } = await supabase
        .from('ordem_de_servico')
        .update({
          status_id: 4,
          assinatura: dados.assinatura,
          rg_assinatura: documentoNumerico ? Number(documentoNumerico) : null,
          ip_assinatura: ipAssinatura,
          updated_at: new Date().toISOString(),
        })
        .eq('id', osId);

      if (error) {
        console.error('Erro ao registrar assinatura da OS:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Erro inesperado ao assinar OS:', err);
      return false;
    }
  },

  /**
   * Busca as medições vinculadas às OS do cliente e vinculadas diretamente ao cadastro do cliente
   */
  async getMedicoesCliente(pessoaId: number): Promise<Medicoes[]> {
    try {
      if (!pessoaId) return [];

      const foundMedicaoMap = new Map<number, Medicoes>();

      // Estratégia 1: Medições diretas onde ordem_de_servico_id guarda o ID da Pessoa (Cliente)
      const { data: directMedicoes, error: dirErr } = await supabase
        .from('medicoes')
        .select('*')
        .eq('ordem_de_servico_id', pessoaId)
        .order('id', { ascending: false });

      if (!dirErr && directMedicoes) {
        for (const m of directMedicoes) {
          foundMedicaoMap.set(m.id, m as Medicoes);
        }
      }

      // Estratégia 2: Pedidos do cliente -> OSs -> medicoes_id
      const { data: pedidos } = await supabase
        .from('pedido')
        .select('id')
        .eq('pessoa_id', pessoaId);

      const pedidoIds = (pedidos || []).map(p => p.id);
      const allSearchIds = Array.from(new Set([...pedidoIds, pessoaId]));

      if (allSearchIds.length > 0) {
        // Buscar OSs do cliente
        const { data: ordens } = await supabase
          .from('ordem_de_servico')
          .select('id, medicoes_id, pedido_id')
          .in('pedido_id', allSearchIds);

        const linkedMedicaoIds = Array.from(
          new Set(
            (ordens || [])
              .map(o => Number(o.medicoes_id))
              .filter(id => id && !isNaN(id) && id > 0)
          )
        );

        if (linkedMedicaoIds.length > 0) {
          const { data: linkedMedicoes, error: linkErr } = await supabase
            .from('medicoes')
            .select('*')
            .in('id', linkedMedicaoIds);

          if (!linkErr && linkedMedicoes) {
            for (const m of linkedMedicoes) {
              foundMedicaoMap.set(m.id, m as Medicoes);
            }
          }
        }
      }

      // Ordenar por ID desc
      const result = Array.from(foundMedicaoMap.values()).sort((a, b) => Number(b.id) - Number(a.id));
      return result;
    } catch (err) {
      console.error('Erro inesperado em getMedicoesCliente:', err);
      return [];
    }
  },

  /**
   * Busca o extrato financeiro (contas a receber) do cliente
   */
  async getExtratoFinanceiroCliente(pessoaId: number): Promise<Conta[]> {
    try {
      const { data, error } = await supabase
        .from('conta')
        .select('*')
        .eq('pessoa_id', pessoaId)
        .eq('tipo_conta_id', 1) // 1 = Conta a Receber
        .order('dt_vencimento', { ascending: false });

      if (error) {
        console.error('Erro ao buscar extrato financeiro do cliente:', error);
        return [];
      }

      return (data || []) as Conta[];
    } catch (err) {
      console.error('Erro inesperado em getExtratoFinanceiroCliente:', err);
      return [];
    }
  },

  /**
   * Obtém estatísticas consolidadas para o dashboard do cliente
   */
  async getDashboardStats(pessoaId: number): Promise<AreaClienteStats> {
    try {
      const [ordens, medicoes, contas, propostas] = await Promise.all([
        this.getOrdensServicoCliente(pessoaId),
        this.getMedicoesCliente(pessoaId),
        this.getExtratoFinanceiroCliente(pessoaId),
        this.getPropostasCliente(pessoaId),
      ]);

      const hoje = new Date().toISOString().split('T')[0];

      // OS counts
      const osEmAberto = ordens.filter(os => os.status_id === 8 || os.status_id === 1).length; // Solicitado / Agendado
      const osEmAndamento = ordens.filter(os => os.status_id === 2 || os.status_id === 6).length; // Em Andamento / Aguardando Assinatura
      const osFinalizadas = ordens.filter(os => os.status_id === 4 || os.status_id === 7 || os.status_id === 9).length; // Finalizado / Medição Gerada / Faturado

      // Medições
      const medicoesEmAnalise = medicoes.filter(m => m.status_medicao_id === 3).length;

      // Financeiro
      let totalPendente = 0;
      let totalPago = 0;
      let totalVencido = 0;

      contas.forEach(c => {
        const val = Number(c.valor || 0);
        if (c.dt_pagamento) {
          totalPago += val;
        } else {
          if (c.dt_vencimento && c.dt_vencimento < hoje) {
            totalVencido += val;
          } else {
            totalPendente += val;
          }
        }
      });

      return {
        totalOS: ordens.length,
        osEmAberto,
        osEmAndamento,
        osFinalizadas,
        totalMedicoes: medicoes.length,
        medicoesEmAnalise,
        totalFinanceiroPendente: totalPendente,
        totalFinanceiroPago: totalPago,
        totalFinanceiroVencido: totalVencido,
        totalPropostas: propostas.length,
      };
    } catch (err) {
      console.error('Erro ao calcular estatísticas do dashboard do cliente:', err);
      return {
        totalOS: 0,
        osEmAberto: 0,
        osEmAndamento: 0,
        osFinalizadas: 0,
        totalMedicoes: 0,
        medicoesEmAnalise: 0,
        totalFinanceiroPendente: 0,
        totalFinanceiroPago: 0,
        totalFinanceiroVencido: 0,
        totalPropostas: 0,
      };
    }
  },

  /**
   * Realiza a solicitação de um novo frete pelo cliente
   * O frete fica registrado na tabela 'ordem_de_servico' com status_id = 8 (Solicitado / Em Aberto)
   */
  async solicitarFrete(payload: SolicitacaoFretePayload): Promise<OrdemDeServico> {
    try {
      let pedidoId = payload.pedidoId;

      // Se não passou pedidoId, busca ou cria um pedido padrão para este cliente
      if (!pedidoId) {
        const { data: pedidos } = await supabase
          .from('pedido')
          .select('id')
          .eq('pessoa_id', payload.pessoaId)
          .order('id', { ascending: false })
          .limit(1);

        if (pedidos && pedidos.length > 0) {
          pedidoId = pedidos[0].id;
        } else {
          // Cria um pedido/contrato inicial para o cliente
          const { data: novoPedido, error: pedCreateErr } = await supabase
            .from('pedido')
            // A tabela `pedido` só tem pessoa_id (+ timestamps). Os campos
            // status/consideracoes/condicoes_* não existem e faziam o insert
            // falhar (PGRST204) — nenhum cliente sem pedido prévio conseguia
            // abrir solicitação pelo portal.
            .insert({ pessoa_id: payload.pessoaId })
            .select()
            .single();

          if (pedCreateErr || !novoPedido) {
            throw new Error(`Falha ao criar vínculo de pedido: ${pedCreateErr?.message}`);
          }
          pedidoId = novoPedido.id;
        }
      }

      // Inserir a nova OS com status_id = 8 (Solicitado / Em Aberto)
      const novaOS = {
        pedido_id: pedidoId,
        status_id: 8, // 8 = 'Solicitado' (Em Aberto)
        descricao: payload.descricao || 'Solicitação de Frete via Portal do Cliente',
        retirada: payload.retirada,
        entrega: payload.entrega,
        data_atendimento: payload.dataAtendimento,
        horario_inicio: payload.horarioInicio || null,
        horario_final: payload.horarioFinal || null,
        tipo_veiculo_id: payload.tipoVeiculoId || null,
        quantidade_ajudante: payload.quantidadeAjudante || 0,
        ocorrencias: payload.observacoes || null,
        detalhes_adicional: payload.detalhesAdicional || null,
        assinatura: payload.assinatura || null,
        rg_assinatura: payload.rg_assinatura || null,
        created_at: new Date().toISOString()
      };

      const { data: osCriada, error: osError } = await supabase
        .from('ordem_de_servico')
        .insert([novaOS])
        .select()
        .single();

      if (osError || !osCriada) {
        throw new Error(`Erro ao registrar solicitação de frete: ${osError?.message}`);
      }

      return osCriada as OrdemDeServico;
    } catch (err: any) {
      console.error('Erro ao solicitar frete:', err);
      throw err;
    }
  }
};
